import type { Gradient } from './gradient';
import { BUILTIN_GRADIENTS } from './gradient';
import type { BlendMode, RGBA } from './types';

export type StrokePosition = 'outside' | 'inside' | 'center';
export type BevelStyle = 'outerBevel' | 'innerBevel' | 'emboss' | 'pillowEmboss';

interface EffectBase {
  enabled: boolean;
  /** 0..1 */
  opacity: number;
  blendMode: BlendMode;
}

export interface DropShadowEffect extends EffectBase {
  color: RGBA;
  /** Degrees, 0 pointing right, counterclockwise positive like canvas. */
  angle: number;
  distance: number;
  /** 0..100, expands the silhouette before blur. */
  spread: number;
  size: number;
}

export interface InnerShadowEffect extends EffectBase {
  color: RGBA;
  angle: number;
  distance: number;
  choke: number;
  size: number;
}

export interface OuterGlowEffect extends EffectBase {
  color: RGBA;
  spread: number;
  size: number;
}

export interface InnerGlowEffect extends EffectBase {
  color: RGBA;
  choke: number;
  size: number;
}

export interface BevelEffect extends EffectBase {
  style: BevelStyle;
  /** Percent strength. */
  depth: number;
  size: number;
  soften: number;
  angle: number;
  /** 0..90 elevation of the light. */
  altitude: number;
  highlightColor: RGBA;
  highlightMode: BlendMode;
  highlightOpacity: number;
  shadowColor: RGBA;
  shadowMode: BlendMode;
  shadowOpacity: number;
}

export interface StrokeEffect extends EffectBase {
  color: RGBA;
  size: number;
  position: StrokePosition;
}

export interface ColorOverlayEffect extends EffectBase {
  color: RGBA;
}

export interface GradientOverlayEffect extends EffectBase {
  gradient: Gradient;
  angle: number;
  scale: number;
  reverse: boolean;
}

export interface PatternOverlayEffect extends EffectBase {
  patternId: string;
  scale: number;
}

/**
 * Photoshop-style layer effects. Missing keys mean that effect is off; an
 * explicit `enabled: false` is what the dialog uses while editing.
 */
export interface LayerEffects {
  dropShadow?: DropShadowEffect;
  innerShadow?: InnerShadowEffect;
  outerGlow?: OuterGlowEffect;
  innerGlow?: InnerGlowEffect;
  bevel?: BevelEffect;
  stroke?: StrokeEffect;
  colorOverlay?: ColorOverlayEffect;
  gradientOverlay?: GradientOverlayEffect;
  patternOverlay?: PatternOverlayEffect;
}

const BLACK: RGBA = { r: 0, g: 0, b: 0, a: 1 };
const WHITE: RGBA = { r: 1, g: 1, b: 1, a: 1 };
const RED: RGBA = { r: 1, g: 0, b: 0, a: 1 };

export function defaultDropShadow(): DropShadowEffect {
  return {
    enabled: true,
    opacity: 0.75,
    blendMode: 'multiply',
    color: BLACK,
    angle: 120,
    distance: 5,
    spread: 0,
    size: 5,
  };
}

export function defaultInnerShadow(): InnerShadowEffect {
  return {
    enabled: true,
    opacity: 0.75,
    blendMode: 'multiply',
    color: BLACK,
    angle: 120,
    distance: 5,
    choke: 0,
    size: 5,
  };
}

export function defaultOuterGlow(): OuterGlowEffect {
  return {
    enabled: true,
    opacity: 0.75,
    blendMode: 'screen',
    color: { r: 1, g: 1, b: 0.7, a: 1 },
    spread: 0,
    size: 5,
  };
}

export function defaultInnerGlow(): InnerGlowEffect {
  return {
    enabled: true,
    opacity: 0.75,
    blendMode: 'screen',
    color: { r: 1, g: 1, b: 0.7, a: 1 },
    choke: 0,
    size: 5,
  };
}

export function defaultBevel(): BevelEffect {
  return {
    enabled: true,
    opacity: 1,
    blendMode: 'normal',
    style: 'innerBevel',
    depth: 100,
    size: 5,
    soften: 0,
    angle: 120,
    altitude: 30,
    highlightColor: WHITE,
    highlightMode: 'screen',
    highlightOpacity: 0.75,
    shadowColor: BLACK,
    shadowMode: 'multiply',
    shadowOpacity: 0.75,
  };
}

export function defaultStroke(): StrokeEffect {
  return {
    enabled: true,
    opacity: 1,
    blendMode: 'normal',
    color: RED,
    size: 3,
    position: 'outside',
  };
}

export function defaultColorOverlay(): ColorOverlayEffect {
  return {
    enabled: true,
    opacity: 1,
    blendMode: 'normal',
    color: RED,
  };
}

export function defaultGradientOverlay(): GradientOverlayEffect {
  return {
    enabled: true,
    opacity: 1,
    blendMode: 'normal',
    gradient: BUILTIN_GRADIENTS[0],
    angle: 90,
    scale: 100,
    reverse: false,
  };
}

export function defaultPatternOverlay(patternId = ''): PatternOverlayEffect {
  return {
    enabled: true,
    opacity: 1,
    blendMode: 'normal',
    patternId,
    scale: 100,
  };
}

export function defaultLayerEffects(): LayerEffects {
  return {};
}

/** True when at least one effect will paint something. */
export function hasActiveEffects(effects: LayerEffects | null | undefined): boolean {
  if (!effects) return false;
  return (
    Boolean(effects.dropShadow?.enabled) ||
    Boolean(effects.innerShadow?.enabled) ||
    Boolean(effects.outerGlow?.enabled) ||
    Boolean(effects.innerGlow?.enabled) ||
    Boolean(effects.bevel?.enabled) ||
    Boolean(effects.stroke?.enabled) ||
    Boolean(effects.colorOverlay?.enabled) ||
    Boolean(effects.gradientOverlay?.enabled) ||
    Boolean(effects.patternOverlay?.enabled)
  );
}

/** Merge persisted partial effects with defaults for each present key. */
export function readLayerEffects(raw: LayerEffects | null | undefined): LayerEffects | null {
  if (!raw || typeof raw !== 'object') return null;
  const out: LayerEffects = {};
  if (raw.dropShadow) out.dropShadow = { ...defaultDropShadow(), ...raw.dropShadow };
  if (raw.innerShadow) out.innerShadow = { ...defaultInnerShadow(), ...raw.innerShadow };
  if (raw.outerGlow) out.outerGlow = { ...defaultOuterGlow(), ...raw.outerGlow };
  if (raw.innerGlow) out.innerGlow = { ...defaultInnerGlow(), ...raw.innerGlow };
  if (raw.bevel) out.bevel = { ...defaultBevel(), ...raw.bevel };
  if (raw.stroke) out.stroke = { ...defaultStroke(), ...raw.stroke };
  if (raw.colorOverlay) out.colorOverlay = { ...defaultColorOverlay(), ...raw.colorOverlay };
  if (raw.gradientOverlay) {
    out.gradientOverlay = { ...defaultGradientOverlay(), ...raw.gradientOverlay };
  }
  if (raw.patternOverlay) {
    out.patternOverlay = { ...defaultPatternOverlay(), ...raw.patternOverlay };
  }
  return Object.keys(out).length > 0 ? out : null;
}

export function shadowOffset(angleDegrees: number, distance: number): { x: number; y: number } {
  const rad = (angleDegrees * Math.PI) / 180;
  return {
    x: Math.cos(rad) * distance,
    y: -Math.sin(rad) * distance,
  };
}
